import { FC, useEffect, useState } from "react";

import classes from "./Image.module.css";
import SectionHeader from "../Layout/SectionHeader";
import CountUp from "react-countup";
import axios from "axios";
import { useSelector } from "react-redux";
import { userState } from "../../store/user";
import { getLang, i18n } from "../../i18n";

interface ArtworkModelDocument {
  serverTime: number;
  _id: string;
  userID: string;
  slot: string;
  imageAmazonS3URL: string;
  title: string;
  profit: number;
  silentVotesCount: number;
  silentVotesWinner: number;
  battleVotesCount: number;
  battleVotesWinner: number;
  level: number;
  description: string;
  aiMentorReview: number;
  aiMentorDescription: string;
  category: string;
  tags: string[];
}

const NFT: FC = () => {
  const lang = getLang();
  const t = i18n.nft[lang];

  const API_URL = process.env.REACT_APP_API_URL;
  const user = useSelector(userState);
  const [art, setArt] = useState<ArtworkModelDocument | null>(null);
  const [screenIsLoading, setScreenIsLoading] = useState(true);
  const [nftID, setNftID] = useState("");
  const level = 8;
  let stars = new Array(level);
  for (let i = 0; i < level; i++) {
    stars.push(true);
  }
  for (let i = level; i < 7; i++) {
    stars.push(false);
  }
  useEffect(() => {
    console.log("nft use effect");
    const search = window.location.search;
    const params = new URLSearchParams(search);
    const queryID = params.get("id");
    if (queryID) {
      setNftID(queryID);
    }

    const getNFTArtwork = async (slot: string) => {
      await axios
        .get(`${API_URL}/artworks/nft/${slot}`, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${user.sessionToken}` },
        })
        .then((response) => {
          setArt(response.data.artwork);
          setScreenIsLoading(false);
        })
        .catch((error) => {
          console.log(error);
        });
    };

    if (queryID) getNFTArtwork(queryID);
  }, []);
  return (
    <>
      <SectionHeader label={t.nft} back="/gallery" />
      <section className={classes.image}>
        <div className="container">
          {!screenIsLoading && art && (
            <>
              <h2>{art.title}</h2>
              <div className={classes.preview}>
                <img
                  src={art.imageAmazonS3URL}
                  alt={art.title}
                  title={art.title}
                />
              </div>

              <div className={classes.half}>
                <div className={classes.halfItem}>
                  <h2>{t.profit}</h2>
                  {art.profit > 0 && (
                    <CountUp
                      className={classes.count}
                      duration={1}
                      start={0.0}
                      end={art.profit}
                      separator=","
                      suffix=" 🌕"
                      decimals={3}
                      decimal="."
                    />
                  )}
                  {art.profit === 0 && (
                    <div className={classes.count}>{t.zeroProfit}</div>
                  )}
                  {art.profit < 0 && (
                    <div className={classes.count}>{art.profit} 🌕</div>
                  )}
                </div>

                {(art.silentVotesCount > 0 || art.battleVotesCount > 0) && (
                  <div className={classes.halfItem}>
                    <div className={classes.rate}>
                      <h2>{t.winRate}</h2>
                      <div className={classes.points}>750/1000</div>
                    </div>
                    <div className={classes.bar}>
                      <div
                        className={classes.barFilled}
                        style={{ width: `${(75 / 100) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>

              <h2>{t.imageLevel}</h2>
              <div className={classes.level}>
                <div className={classes.stars}>
                  {stars.map((star) => (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 27 27"
                      fill="none"
                    >
                      <path
                        d="M8.53379 17.014L7.67934 21.9738C7.67934 22.4869 7.67934 23 8.53379 23C8.83874 23 12.6351 20.7766 12.9769 20.6056C13.7972 20.1951 14.6858 20.4346 15.0276 20.6056C15.3694 20.7766 19.1657 23 19.4707 23C20.3251 23 20.3251 22.4869 20.3251 21.9738L19.4707 17.014C19.4707 16.8429 19.2998 16.3299 19.8125 15.8168L23.572 12.0541C23.9708 11.598 24.4265 10.8569 23.2302 10.6859L17.7572 9.83074C17.0737 9.65971 16.7319 8.97559 16.561 8.63354L14.6812 4.52883C14.1344 3.57107 13.542 4.12976 13.3141 4.52883L11.2635 8.63354C11.0926 8.97559 10.9217 9.65971 10.2381 9.83074L4.76975 10.6859C3.57353 10.8569 4.02923 11.598 4.42798 12.0541L8.18754 15.8168C8.7002 16.3299 8.53379 16.8429 8.53379 17.014Z"
                        fill={star ? "#3095dd" : "#d2e9fb"}
                      />
                    </svg>
                  ))}
                </div>
                {/* NFT logo SVG */}
              </div>

              {art.description && (
                <>
                  <h2>{t.description}</h2>
                  <div className={classes.description}>{art.description}</div>
                </>
              )}

              {art.aiMentorReview && (
                <>
                  <div className={classes.rate}>
                    <h2>{t.aiMentorReview}</h2>
                    <div className={classes.points}>
                      {(art.aiMentorReview * 10).toFixed(1)} / 10
                    </div>
                  </div>
                  <div className={classes.bar}>
                    <div
                      className={classes.barFilled}
                      style={{ width: `${art.aiMentorReview * 100}%` }}
                    ></div>
                  </div>
                </>
              )}

              <div className={classes.rate}>
                <h2>{t.category}</h2>
              </div>
              <div className={classes.description}>{art.category}</div>

              <div className={classes.rate}>
                <h2>{t.tags}</h2>
                <div className={classes.points}>{art.tags?.length} / 100</div>
              </div>
              <div className={classes.tags}>
                {art.tags?.map((tag, index) => (
                  <div key={`tag-${index}`} className={classes.tag}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 16 15"
                      fill="none"
                    >
                      <path
                        d="M3.14697 8.20187L5.80826 10.5882L12.8529 4.41177"
                        stroke="#868686"
                        strokeWidth="1.11111"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    {tag}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </>
  );
};

export default NFT;
